import React, { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { config } from '../../config/environment'
import { EnvironmentBadge } from '../ui/EnvironmentBadge'
import Breadcrumb from './Breadcrumb'
import UserMenu from './UserMenu'
import CommandMenu from './CommandMenu'
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet'
import Sidebar from './Sidebar'
import {
  Menu,
  Search,
  Bell,
  Code,
} from 'lucide-react'

interface TopBarProps {
  onSidebarToggle: () => void
  sidebarCollapsed: boolean
}

export default function TopBar({ onSidebarToggle, sidebarCollapsed }: TopBarProps) {
  const { logout, partner } = useAuth()
  const [commandOpen, setCommandOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const partnerName = partner?.partnerName || 'Partner'

  return (
    <>
      {/* Environment Badge - shows DEV/BETA indicator */}
      <EnvironmentBadge position="banner" showDetails={true} />

      {/* Dev Mode Banner */}
      {config.features.devMode && (
        <div className="bg-yellow-400 text-yellow-900 px-4 py-1 text-xs font-semibold text-center flex items-center justify-center gap-2">
          <Code className="h-3.5 w-3.5" />
          <span>Auth Bypassed - Using mock data</span>
        </div>
      )}

      {/* Main TopBar */}
      <header className="h-14 bg-white border-b border-slate-200 shrink-0 z-10">
        <div className="h-full px-4 flex items-center justify-between gap-4">
          {/* Left side */}
          <div className="flex items-center gap-3 min-w-0">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 -ml-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Desktop sidebar toggle */}
            <button
              onClick={onSidebarToggle}
              className="hidden lg:flex p-2 -ml-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Breadcrumbs */}
            <Breadcrumb className="hidden sm:flex" />
          </div>

          {/* Right side */}
          <div className="flex items-center gap-1">
            {/* Search button */}
            <button
              onClick={() => setCommandOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            >
              <Search className="h-4 w-4" />
              <span className="hidden md:inline text-slate-400">Search...</span>
              <kbd className="hidden md:inline-flex h-5 items-center gap-0.5 rounded border border-slate-200 bg-slate-50 px-1.5 text-[10px] font-medium text-slate-400">
                <span className="text-xs">⌘</span>K
              </kbd>
            </button>

            {/* Notifications placeholder */}
            <button className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors relative">
              <Bell className="h-5 w-5" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-orange-500 rounded-full" />
            </button>

            {/* Separator */}
            <div className="h-6 w-px bg-slate-200 mx-1 hidden sm:block" />

            {/* User menu */}
            <UserMenu
              partnerName={partnerName}
              partnerId={partner?.partnerId}
              onLogout={logout}
            />
          </div>
        </div>
      </header>

      {/* Command Menu */}
      <CommandMenu open={commandOpen} onOpenChange={setCommandOpen} />

      {/* Mobile Sidebar Sheet */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="p-0 w-[280px]">
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          <MobileSidebar onClose={() => setMobileMenuOpen(false)} />
        </SheetContent>
      </Sheet>
    </>
  )
}

// ─── Mobile Sidebar (rendered inside a Sheet) ────────────────────────

function MobileSidebar({ onClose }: { onClose: () => void }) {
  // Re-use the Sidebar component but force it to expanded mode
  // We wrap it so clicks on nav links close the sheet
  return (
    <div className="h-full" onClick={(e) => {
      // Close mobile menu when a link is clicked
      const target = e.target as HTMLElement
      if (target.closest('a')) {
        onClose()
      }
    }}>
      <Sidebar collapsed={false} onToggle={() => {}} />
    </div>
  )
}
